import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { ChevronLeft, Minus, Plus, MessageSquare, Truck, ShieldCheck, Package, MapPin } from "lucide-react";
import * as ella from "@/lib/ellaApi";
import { fmtIDR, productImg } from "@/lib/ellaApi";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import { useSeo, productJsonLd } from "@/lib/seo";
import { toast } from "sonner";
import ProductImageGallery from "@/components/ProductImageGallery";
import { Textarea } from "@/components/ui/textarea";

export default function ProductPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const ctx = useOutletContext();
  const { isAuthed } = useAuth();
  const { addToCart, updateCartQty, cartItems } = useStore();
  const [detail, setDetail] = useState(null);
  const [images, setImages] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [defaultAddr, setDefaultAddr] = useState(null);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setImages([]);
    Promise.all([
      ella.getProductDetail(id),
      ella.getProductImages(id),
      ella.getProductPrices(id),
    ]).then(([d, im, pr]) => {
      if (!active) return;
      if (d.status) { setDetail(d.data); setQty(Number(d.data.MinOrder || 1)); }
      if (im.status) setImages(im.data || []);
      if (pr.status) setTiers(pr.data || []);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);



  // Legacy product screen also shows where the order would ship (default address)
  useEffect(() => {
    if (!isAuthed) { setDefaultAddr(null); return; }
    ella.getAddresses().then((res) => {
      if (res.status && res.data?.length) {
        setDefaultAddr(res.data.find((a) => Number(a.IsDefault) === 1) || res.data[0]);
      }
    }).catch(() => {});
  }, [isAuthed]);

  const openChat = () => {
    if (!detail) return;
    if (!isAuthed) { ctx?.openAuth?.(); return; }
    // Tokopedia-style: land in the chat with a contextual message ready to send/edit.
    // Single line — the composer is an <input> that strips newlines.
    const prefill = `Halo Admin ${detail.Branch} 👋 Saya mau tanya produk "${detail.Product}". Apakah stoknya tersedia?`;
    nav(`/messages/${detail.BranchID}`, { state: { branchName: detail.Branch, prefill } });
  };

  const ogImagePath = images?.[0]?.ImagePath || detail?.ImagePath;
  useSeo({
    title: detail ? detail.Product : "Detail Produk",
    description: detail
      ? `${detail.Product} — ${ella.fmtIDR(ella.effectivePrice(detail.Price, detail.DiscountType, detail.Discount))} dari cabang ${detail.Branch}. ${(detail.Description || "").slice(0, 120)}`
      : undefined,
    path: `/product/${id}`,
    image: ogImagePath ? productImg(ogImagePath) : undefined,
    jsonLd: detail
      ? productJsonLd(detail, images, productImg, ella.effectivePrice(detail.Price, detail.DiscountType, detail.Discount))
      : null,
  });

  const inCart = cartItems.find((i) => i.ProductID === id);
  const cartQty = Number(inCart?.Qty || 0);

  const handleAdd = async () => {
    if (!detail || qty <= 0 || busy) return;
    setBusy(true);
    try {
      const r = await addToCart(detail, qty, notes);
      if (r.ok) toast.success(`${qty} item ditambahkan ke keranjang`);
      else toast.error(r.message || "Gagal menambah ke keranjang");
    } catch (e) {
      toast.error("Gagal menambah ke keranjang");
    } finally {
      setBusy(false);
    }
  };

  // In-cart mode: the stepper edits the CART quantity directly (same pattern
  // as ProductCard) while the button label stays a constant "+ Keranjang".
  const handleCartDelta = async (delta) => {
    if (!detail || busy) return;
    const next = Math.max(0, cartQty + delta);
    if (next > Number(detail.Stock || 0)) { toast.warning(`Stock hanya tersedia ${detail.Stock}`); return; }
    setBusy(true);
    try {
      const r = await updateCartQty(id, next, notes || inCart?.Notes || "");
      if (r?.message) toast.warning(r.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading || !detail) {
    return <div className="p-4 space-y-3">
      <div className="aspect-square w-full skeleton rounded-2xl" />
      <div className="h-6 w-2/3 skeleton rounded-md" />
      <div className="h-4 w-1/3 skeleton rounded-md" />
    </div>;
  }

  const price = Number(detail.Price || 0);
  const finalPrice = ella.effectivePrice(detail.Price, detail.DiscountType, detail.Discount);
  const hasDiscount = ella.hasDiscount(detail.DiscountType);
  const stockOut = Number(detail.Stock || 0) <= 0;

  const allImages = images.length ? images : [{ ID: "default", ImagePath: detail.ImagePath || "" }];

  return (
    <div className="pb-32">
      <div style={{ top: "var(--app-header-height, 60px)" }} className="px-4 py-3 flex items-center gap-3 sticky bg-white z-30 border-b border-gray-100">
        <button onClick={() => nav(-1)} className="p-1 -ml-1 hover:bg-gray-100 rounded-full" data-testid="back-button">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-display text-base font-bold text-gray-900 truncate flex-1">Detail Produk</h1>
      </div>

      {/* Image gallery */}
      <ProductImageGallery images={allImages} productName={detail.Product} />

      <div className="px-4 mt-3 space-y-3">
        <div>
          <h2 className="font-display text-xl font-bold text-gray-900 leading-tight" data-testid="product-name">{detail.Product}</h2>
          <p className="text-xs text-gray-500 mt-0.5">Cabang {detail.Branch}</p>
        </div>

        <div className="flex items-baseline gap-2">
          {hasDiscount && <span className="text-sm text-gray-400 line-through">{fmtIDR(price)}</span>}
          <span className="font-display text-2xl font-bold text-[#D32F2F]" data-testid="product-price">{fmtIDR(finalPrice)}</span>
          {hasDiscount && (
            <span className="text-[10px] font-bold bg-[#D32F2F] text-white px-1.5 py-0.5 rounded">
              {ella.discountLabel(detail.DiscountType, detail.Discount, fmtIDR)}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span className="flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Stok: <b className={stockOut ? "text-red-600" : "text-emerald-600"}>{detail.Stock}</b></span>
          <span className="flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Min. order {detail.MinOrder}</span>
        </div>

        {defaultAddr && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500 bg-gray-50 rounded-xl p-2.5" data-testid="product-delivery-address">
            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#D32F2F]" />
            <span className="line-clamp-2">
              Dikirim ke <b className="text-gray-700">{defaultAddr.Name}</b> — {defaultAddr.DistrictName}, {defaultAddr.CityName}
            </span>
          </div>
        )}

        {/* Tiered pricing */}
        {tiers.length > 0 && (
          <div className="bg-red-50 rounded-2xl p-3">
            <p className="text-xs font-bold text-[#D32F2F] uppercase tracking-wider mb-2">Harga Grosir</p>
            <div className="space-y-1.5">
              {tiers.map((t) => (
                <div key={t.ID} className="flex justify-between text-sm">
                  <span className="text-gray-700">{t.MinOrder} – {t.MaxOrder} pcs</span>
                  <b className="text-gray-900">{fmtIDR(t.Price)} / pcs</b>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        {detail.Description && (
          <div>
            <h3 className="font-display font-bold text-sm text-gray-900 mb-1">Deskripsi Produk</h3>
            <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{detail.Description}</p>
          </div>
        )}

        <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-xl p-2.5">
          <ShieldCheck className="w-4 h-4" /> Produk halal, dijaga rantai dingin sampai ke tangan Anda.
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs font-semibold text-gray-700">Catatan (opsional)</label>
          <Textarea
            placeholder="Contoh: tolong dikirim sore"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 resize-none"
            data-testid="product-notes-input"
            rows={2}
          />
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-gray-100 px-4 py-3 z-40 flex items-center gap-3">
        <button
          onClick={openChat}
          className="w-11 h-11 rounded-full border border-red-200 text-[#D32F2F] flex items-center justify-center shrink-0 hover:bg-red-50"
          title={`Chat Admin ${detail.Branch}`}
          data-testid="product-chat-button"
        >
          <MessageSquare className="w-5 h-5" />
        </button>
        {cartQty > 0 ? (
          /* item is in the cart → stepper shows & edits the cart quantity */
          <div className="flex items-center gap-2 bg-red-50 rounded-full p-1" data-testid="product-cart-stepper">
            <button onClick={() => handleCartDelta(-1)} disabled={busy} className="w-8 h-8 rounded-full bg-white text-[#D32F2F] flex items-center justify-center" data-testid="product-qty-decrease">
              <Minus className="w-4 h-4" />
            </button>
            <span className="min-w-[34px] text-center leading-none" data-testid="product-qty-value">
              <span className="block font-bold text-[#D32F2F]">{cartQty}</span>
              <span className="block text-[8px] text-red-400 font-semibold uppercase tracking-wide">keranjang</span>
            </span>
            <button onClick={() => handleCartDelta(1)} disabled={busy} className="w-8 h-8 rounded-full bg-[#D32F2F] text-white flex items-center justify-center" data-testid="product-qty-increase">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        ) : (
          /* not in cart yet → stepper picks how many to add */
          <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
            <button onClick={() => setQty(Math.max(Number(detail.MinOrder || 1), qty - 1))} className="w-8 h-8 rounded-full bg-white text-gray-700 flex items-center justify-center" data-testid="product-qty-decrease">
              <Minus className="w-4 h-4" />
            </button>
            <span className="min-w-[28px] text-center font-bold" data-testid="product-qty-value">{qty}</span>
            <button onClick={() => setQty(Math.min(Number(detail.Stock || 999), qty + 1))} className="w-8 h-8 rounded-full bg-[#D32F2F] text-white flex items-center justify-center" data-testid="product-qty-increase">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* constant, short label — the stepper is what signals the cart state */}
        <button
          onClick={cartQty > 0 ? () => handleCartDelta(1) : handleAdd}
          disabled={busy || stockOut}
          className="flex-1 btn-primary h-11 px-4 flex items-center justify-center gap-2"
          data-testid="product-buy-button"
        >
          {stockOut ? "Stok Habis" : "+ Keranjang"}
        </button>
      </div>
    </div>
  );
}
